// src/pages/personal/analytics/MarketingSection/hooks/useMarketingExpenses.ts
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';


// Interface for marketing expense
export interface MarketingExpense {
  id?: number;
  business: number;
  channel: string;
  platform: string;
  campaign_name?: string;
  amount: number;
  start_date: string;
  end_date: string;
  impressions?: number;
  clicks?: number;
  created_at?: string;
  updated_at?: string;
}

// Parameters for getting expenses list
export interface GetExpensesParams {
  start_date?: string;
  end_date?: string;
  channel?: string;
}

// Interface for hook return values
interface UseMarketingExpensesReturn {
  expenses: MarketingExpense[];
  isLoading: boolean;
  error: string | null;
  fetchExpenses: (params?: GetExpensesParams) => Promise<void>;
  createExpense: (expense: Omit<MarketingExpense, 'id' | 'business'>) => Promise<boolean>;
  updateExpense: (id: number, expense: Partial<MarketingExpense>) => Promise<boolean>;
  deleteExpense: (id: number) => Promise<boolean>;
}

export const useMarketingExpenses = (): UseMarketingExpensesReturn => {
  const { t } = useTranslation(['expenses', 'errors']);
  const { businessId } = useBusinessId();
  
  const [expenses, setExpenses] = useState<MarketingExpense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get list of expenses
  const fetchExpenses = useCallback(async (params?: GetExpensesParams) => {
    if (!businessId) return;

    setIsLoading(true);
    setError(null);

    try {
      const queryParams = {
        business: businessId.toString(),
        ...params
      };
      
      const response = await api.get('/api/analytics/marketing/expenses/', queryParams);
      
      if (response) {
        setExpenses(response);
      } else {
        setError(t('errors:data_loading_error'));
      }
    } catch (error: any) {
      console.error('Error fetching marketing expenses:', error);
      setError(error.message || t('errors:data_loading_error'));
    } finally {
      setIsLoading(false);
    }
  }, [businessId, t]);

  // Create a new expense
  const createExpense = useCallback(async (expense: Omit<MarketingExpense, 'id' | 'business'>): Promise<boolean> => {
    if (!businessId) return false;

    setIsLoading(true);
    setError(null);

    try {
      // Убедимся, что даты правильно форматируются для API
      const formattedExpense = {
        ...expense,
        // Добавляем business как часть данных, так как API требует его
        business: businessId,
        // Используем правильный формат даты для Django
        start_date: new Date(expense.start_date).toISOString(),
        end_date: new Date(expense.end_date).toISOString()
      };
      
      // Логирование для отладки
      console.log('Sending expense data:', formattedExpense);
      
      // Отправляем запрос с бизнес ID в URL как query параметр
      // И в теле запроса
      const url = `/api/analytics/marketing/expenses/?business=${businessId}`;
      
      // Делаем запрос
      await api.post(url, formattedExpense);
      
      // Update expense list
      await fetchExpenses();
      return true;
    } catch (error: any) {
      console.error('Error creating marketing expense:', error);
      setError(error.message || t('errors:data_saving_error'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [businessId, fetchExpenses, t]);

  // Update expense
  const updateExpense = useCallback(async (id: number, expense: Partial<MarketingExpense>): Promise<boolean> => {
    if (!businessId) return false;
    
    setIsLoading(true);
    setError(null);

    try {
      // Подготовим обновленные данные с правильными форматами дат
      const formattedExpense = { 
        ...expense,
        business: businessId // Явно добавляем ID бизнеса
      };
      
      // Форматируем даты, если они есть в обновляемых данных
      if (formattedExpense.start_date) {
        formattedExpense.start_date = new Date(formattedExpense.start_date).toISOString();
      }
      
      if (formattedExpense.end_date) {
        formattedExpense.end_date = new Date(formattedExpense.end_date).toISOString();
      }
      
      // Логирование для отладки
      console.log('Updating expense data:', formattedExpense);
      
      // Добавляем business ID в URL как query параметр
      const url = `/api/analytics/marketing/expenses/${id}/?business=${businessId}`;
      
      // Делаем запрос с business в URL и в теле
      await api.put(url, formattedExpense);
      
      // Update expense list
      await fetchExpenses();
      return true;
    } catch (error: any) {
      console.error('Error updating marketing expense:', error);
      setError(error.message || t('errors:data_saving_error'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [businessId, fetchExpenses, t]);

  // Delete expense
  const deleteExpense = useCallback(async (id: number): Promise<boolean> => {
    if (!businessId) return false;
    
    setIsLoading(true);
    setError(null);

    try {
      // Добавляем business ID в URL как query-параметр
      await api.delete(`/api/analytics/marketing/expenses/${id}/?business=${businessId}`);
      
      // Update expense list
      await fetchExpenses();
      return true;
    } catch (error: any) {
      console.error('Error deleting marketing expense:', error);
      setError(error.message || t('errors:data_delete_error'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [businessId, fetchExpenses, t]);

  return {
    expenses,
    isLoading,
    error,
    fetchExpenses,
    createExpense,
    updateExpense,
    deleteExpense
  };
};